import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { Editor } from '@tiptap/react';
import { format } from 'date-fns';
import { ChatMessageListItem } from 'src/@types/chat';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { Iconify } from 'src/components/iconify';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';

const ChatMessageContentStyled = styled(ConversationMessageContent)(() => ({
  '&:not(.expanded)': {
    maxWidth: '50ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    width: 'auto',
  },
}));

const formatDate = (value: string, locale: Locale) =>
  format(new Date(value), 'dd/MM/yyyy hh:mm a', { locale });

interface IProps {
  message: ChatMessageListItem;
  editor: Editor;
  unpinMessage: (message: ChatMessageListItem) => void;
}

export const PinnedMessageItem = ({
  message,
  editor,
  unpinMessage,
}: IProps) => {
  const { setGoToMessage } = useChatConversationContext();
  const { translate, currentLang } = useLocales();

  const author = useWorkspaceUser(message.author);

  const handleGoToMessage = async () => {
    setGoToMessage(message._id);
  };

  return (
    <MenuItem
      key={message._id}
      sx={(theme) => ({
        height: 'auto',
        alignItems: 'start',
        display: 'flow',
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
          {!!author && (
            <UserProfileShortPopoverTrigger userId={author._id}>
              <CPAvatar
                key={author._id}
                name={author.name}
                picture={author.picture || ''}
                status={author.status}
              />
            </UserProfileShortPopoverTrigger>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
            <Typography variant="subtitle1">{author.name}</Typography>
            <Typography variant="caption" color="text.secondary" pt={0.5}>
              {formatDate(message.createdAt, currentLang.dateValue)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant="text" onClick={handleGoToMessage}>
            <Typography
              variant="caption"
              sx={(theme) => ({ color: theme.palette.primary.main })}
            >
              {translate('communicate.chat.go_to_messages')}
            </Typography>
          </Button>
          <Tooltip title={translate('communicate.chat.unpin')}>
            <IconButton
              onClick={() => {
                unpinMessage(message);
              }}
            >
              <Iconify icon="tabler:pinned-off" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ ml: 6 }}>
        <ChatMessageContentStyled editor={editor} message={message} />
      </Box>
    </MenuItem>
  );
};
